/**
 * apps/command/src/screens/GovernorHome.tsx — §15.7's «Rahbar koʻrinishi».
 *
 * ============================================================================
 * "THE GOVERNOR MUST NOT LAND IN A COCKPIT."
 * ============================================================================
 *
 * §15.7 is unusually specific about this screen, and every line of it is a
 * constraint rather than a suggestion: "He visits once or twice a day and needs
 * the truth in ten seconds… No configuration, no dense tables, nothing that can
 * be broken by a curious click."
 *
 * So: one line of state, four figures, today's exceptions IN SENTENCES, and one
 * button. What is deliberately ABSENT is as much of the design as what is here
 * — there is no table, no filter, no bulk action and no navigation into
 * anything that could be misconfigured.
 *
 * ============================================================================
 * TWO OF THE FOUR FIGURES ARE «hali yoʻq», AND THAT IS THE POINT.
 * ============================================================================
 *
 * Attendance is P3 and the alert engine is P1, so `staff_present` and
 * `open_alerts` arrive as `null` from the server and render as «hali yoʻq».
 * Showing 0 would tell the governor that nobody in the city is at work and that
 * nothing is wrong — and he would believe it, because it is his own dashboard.
 * §15.10's rule that a no-data state must say why exists for exactly this.
 *
 * The map §15.7 asks for ("the city map, large, with status colour and nothing
 * else competing") is NOT here in P0: `mahallas` carries no coordinates yet —
 * §8.1 adds them in P3's directory work — so a map would be 33 pins at invented
 * positions, which is worse than no map. Recorded as D-MBT-06 in
 * docs/mbt-progress.md. Its space is held by the exception list, which is the
 * part §15.7 says he actually reads.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel, ageParts, integer } from '../lib/format.js';
import { statusOf, useFleet } from '../realtime/useFleet.js';
import { Button, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { BarRow, Sparkline, StatusDonut } from '../ui/charts.jsx';
import {
  IconAds,
  IconBroadcast,
  IconCamera,
  IconDavomat,
  IconFleet,
  IconMap,
} from '../ui/icons.jsx';
import { Distribution, KpiCard, SystemPulse, Ticker } from '../ui/console.jsx';
import { BarChart, DualSeries, Gauge } from '../ui/charts.jsx';
import {
  DataTable,
  HeroBanner,
  KpiTile,
  LiveBadge,
  ProgressBar,
  RankBadge,
  SegmentedControl,
  StatChip,
  type Column,
  type Tone,
} from '../ui/data.jsx';

type Overview = components['schemas']['MbtOverview'];
type Summary = components['schemas']['AdminSummary'];
type Timeseries = components['schemas']['AdminTimeseriesResponse'];
type MahallaStats = components['schemas']['AdminMahallaStatsResponse'];
type Breakdown = components['schemas']['AdminBreakdownResponse'];

/**
 * §4.3's triage order as a number, so "worst wins" is one comparison.
 *
 * Read through `rankOf` rather than indexed directly: an unknown status must
 * sort as the LEAST alarming rather than crash or, worse, quietly outrank a
 * genuine critical because `undefined` compared strangely.
 */
const RANK_OF: Record<string, number> = {
  healthy: 0,
  maintenance: 1,
  warning: 2,
  offline: 3,
  critical: 4,
};

const TONE_OF: Record<string, Tone> = {
  healthy: 'ok',
  maintenance: 'info',
  warning: 'warn',
  offline: 'offline',
  critical: 'crit',
};

const rankOf = (status: string): number => RANK_OF[status] ?? 0;
const toneOf = (status: string): Tone => TONE_OF[status] ?? 'neutral';

type Sort = 'appeals' | 'uptime' | 'risk' | 'name';

/** The six destinations, each gated by the capability its route already checks. */
const QUICK_ACTIONS = [
  { to: '/broadcast', labelKey: 'nav.broadcast', capability: 'mbt.overview.read', Icon: IconBroadcast },
  { to: '/camera', labelKey: 'nav.camera', capability: 'mbt.live.view', Icon: IconCamera },
  { to: '/map', labelKey: 'nav.map', capability: 'mbt.fleet.read', Icon: IconMap },
  { to: '/fleet', labelKey: 'nav.fleet', capability: 'mbt.fleet.read', Icon: IconFleet },
  { to: '/davomat', labelKey: 'nav.davomat', capability: 'mbt.attendance.read', Icon: IconDavomat },
  { to: '/ads', labelKey: 'nav.ads', capability: 'mbt.ads.manage', Icon: IconAds },
] as const;

export function GovernorHome(): React.JSX.Element {
  const { expire, me, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [failed, setFailed] = useState(false);
  /**
   * The queue-depth pulse, sampled as this screen polls.
   *
   * IN-SESSION ONLY, and the panel header says so. There is no stored history
   * for this number, and a sparkline that silently restarted on every page load
   * while looking like a day's history would be a chart that lies quietly —
   * worse than the absence it replaced. It is labelled as a live trace instead.
   */
  const [pulse, setPulse] = useState<readonly number[]>([]);
  /**
   * Default: RISK. Not alphabetical and not "biggest first".
   *
   * §15.7 asks the executive home to lead with what is wrong. A table that
   * opens sorted by name makes the governor hunt for the failing mahalla; one
   * that opens sorted by risk has already answered the question they came with.
   */
  const [sort, setSort] = useState<Sort>('risk');
  /**
   * THE ANALYTICS STACK, WHICH THIS PANEL HAD NEVER ONCE CALLED.
   *
   * `/admin/summary`, `/admin/stats/timeseries`, `/admin/stats/mahallas` and
   * `/admin/stats/breakdown` have existed the whole time and no screen touched
   * one of them. Between them they carry the province's real appeal figures —
   * a 366-bucket daily series, per-mahalla resolution times, five zero-filled
   * distributions — which is precisely the material this screen was missing
   * while it showed four counts of kiosks.
   *
   * Each is held separately and each may be null: `analytics.read` is a
   * different capability from `mbt.overview.read`, so a fleet operator gets the
   * kiosk half of this screen and no appeal figures at all. A missing section
   * is rendered as missing rather than as zero.
   */
  const [summary, setSummary] = useState<Summary | null>(null);
  const [series, setSeries] = useState<Timeseries | null>(null);
  const [mahallaStats, setMahallaStats] = useState<MahallaStats | null>(null);
  const [breakdown, setBreakdown] = useState<Breakdown | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const data = unwrap(await api().GET('/mbt/overview'));
        if (!cancelled) {
          setOverview(data as Overview);
          setFailed(false);
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) onExpired();
        // The previous figures are NOT cleared. They stay true as of the stamp
        // beneath them; blanking a governor's dashboard because one poll failed
        // would turn a transient network blip into an apparent emergency.
        else setFailed(true);
      }
    };
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onExpired]);

  /**
   * The analytics stack, fetched once and refreshed slowly.
   *
   * Five minutes, not thirty seconds: these are day-bucketed aggregates over a
   * month. Polling them at the fleet's cadence would put four analytical
   * queries a second onto a shared box to redraw a chart whose newest bucket
   * changes a few times an hour.
   *
   * Each settles INDEPENDENTLY. A session without `analytics.read` gets a 403
   * on all four and still sees the whole fleet half of this screen — one
   * refused capability must not blank a governor's dashboard.
   */
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      const settle = async <T,>(run: () => Promise<T>, set: (value: T) => void): Promise<void> => {
        try {
          const value = await run();
          if (!cancelled) set(value);
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) onExpired();
          // 403 is not an error here — it is this operator's scope.
        }
      };
      await Promise.all([
        settle(async () => unwrap(await api().GET('/admin/summary', {})) as Summary, setSummary),
        settle(
          async () =>
            unwrap(
              await api().GET('/admin/stats/timeseries', { params: { query: { bucket: 'day' } } }),
            ) as Timeseries,
          setSeries,
        ),
        settle(
          async () => unwrap(await api().GET('/admin/stats/mahallas', {})) as MahallaStats,
          setMahallaStats,
        ),
        settle(
          async () => unwrap(await api().GET('/admin/stats/breakdown', {})) as Breakdown,
          setBreakdown,
        ),
      ]);
    };
    void load();
    const timer = setInterval(() => void load(), 300_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [onExpired]);

  /** The ticker's cursor. Five seconds, matching the house console. */
  useEffect(() => {
    const timer = setInterval(() => setTick((previous) => previous + 1), 5_000);
    return () => clearInterval(timer);
  }, []);

  /**
   * Sample the province's queue depth once per fleet poll.
   *
   * Capped at 40 points: a sparkline this size cannot show more, and an
   * unbounded array on a screen a governor leaves open all day is a slow leak.
   */
  useEffect(() => {
    if (fleet.fetchedAt === null) return;
    const total = fleet.kiosks.reduce(
      (sum, kiosk) => sum + (kiosk.telemetry?.work?.queue_depth ?? 0),
      0,
    );
    setPulse((previous) => [...previous, total].slice(-40));
  }, [fleet.fetchedAt, fleet.kiosks]);

  // §15.7 — "Bugungi diqqat markazida: only what is wrong today, in sentences,
  // not table rows." Built from the fleet the panel already has rather than
  // from a second endpoint, so the sentences and the tiles can never disagree.
  const exceptions = fleet.kiosks
    .map((kiosk) => ({ kiosk, status: statusOf(kiosk, fleet.now) }))
    .filter((entry) => entry.status === 'offline' || entry.status === 'critical')
    .map(({ kiosk }) => {
      if (!kiosk.telemetry_at) {
        return {
          id: kiosk.id,
          text: t('overview.kioskNeverReported', {
            code: kiosk.short_code,
            mahalla: kiosk.mahalla_name,
          }),
        };
      }
      const age = ageParts(new Date(kiosk.telemetry_at), fleet.now);
      return {
        id: kiosk.id,
        text: t('overview.kioskOffline', {
          code: kiosk.short_code,
          mahalla: kiosk.mahalla_name,
          duration: t(age.key, { n: age.n }),
        }),
      };
    });

  /** §4.3 statuses in the order an operator triages them, worst first. */
  const STATUS_ORDER = ['critical', 'offline', 'warning', 'maintenance', 'healthy'] as const;
  const STATUS_TONE: Record<string, string> = {
    critical: 'text-crit',
    offline: 'text-offline',
    warning: 'text-warn',
    maintenance: 'text-brand',
    healthy: 'text-ok',
  };

  const statusCounts = new Map<string, number>();
  for (const kiosk of fleet.kiosks) {
    statusCounts.set(statusOf(kiosk, fleet.now), (statusCounts.get(statusOf(kiosk, fleet.now)) ?? 0) + 1);
  }

  const queueTotal = fleet.kiosks.reduce(
    (sum, kiosk) => sum + (kiosk.telemetry?.work?.queue_depth ?? 0),
    0,
  );

  /**
   * The province rolled up by mahalla — the table this screen is really for.
   *
   * Built from the fleet the panel already holds rather than from a second
   * endpoint, so the ranking and the tiles can never disagree about the same
   * number. That is the same rule the exception sentences below follow, and it
   * matters more here: a row that says a mahalla is fine beside a tile that
   * says it is not is worse than either one being absent.
   */
  interface MahallaRow {
    id: string;
    name: string;
    kiosks: number;
    online: number;
    appeals: number;
    queue: number;
    uptime: number;
    trend: number[];
    worst: string;
  }

  const byMahalla = new Map<string, MahallaRow>();
  for (const kiosk of fleet.kiosks) {
    const status = statusOf(kiosk, fleet.now);
    const current = byMahalla.get(kiosk.mahalla_id) ?? {
      id: kiosk.mahalla_id,
      name: kiosk.mahalla_name,
      kiosks: 0,
      online: 0,
      appeals: 0,
      queue: 0,
      uptime: 0,
      trend: [],
      worst: 'healthy',
    };
    current.kiosks += 1;
    if (status !== 'offline') current.online += 1;
    current.appeals += kiosk.telemetry?.work?.appeals_today ?? 0;
    current.queue += kiosk.telemetry?.work?.queue_depth ?? 0;
    // WORST kiosk wins the row's status, never an average. A mahalla with four
    // healthy machines and one dead one is a mahalla with a dead machine, and
    // averaging that to «80% healthy» is how it stays dead.
    if (rankOf(status) > rankOf(current.worst)) current.worst = status;
    byMahalla.set(kiosk.mahalla_id, current);
  }
  const mahallaRows = [...byMahalla.values()].map((row) => ({
    ...row,
    uptime: row.kiosks === 0 ? 0 : Math.round((row.online / row.kiosks) * 100),
  }));

  const sorted = [...mahallaRows].sort((a, b) => {
    if (sort === 'appeals') return b.appeals - a.appeals;
    if (sort === 'uptime') return a.uptime - b.uptime;
    if (sort === 'risk') return rankOf(b.worst) - rankOf(a.worst) || a.uptime - b.uptime;
    return a.name.localeCompare(b.name);
  });

  const columns: Column<MahallaRow>[] = [
    {
      key: 'rank',
      label: '',
      width: '3rem',
      render: (row) => <RankBadge rank={sorted.indexOf(row) + 1} />,
    },
    {
      key: 'name',
      label: t('overview.colMahalla'),
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink-100">{row.name}</p>
          <p className="text-micro text-ink-500">
            {t('overview.colKiosks')}: {row.online}/{row.kiosks}
          </p>
        </div>
      ),
    },
    {
      key: 'appeals',
      label: t('overview.colAppeals'),
      numeric: true,
      render: (row) => <span className="font-semibold text-ok">{integer(row.appeals)}</span>,
    },
    {
      key: 'queue',
      label: t('overview.colQueue'),
      numeric: true,
      render: (row) => (
        <span className={row.queue > 0 ? 'font-semibold text-crit' : 'text-ink-300'}>
          {integer(row.queue)}
        </span>
      ),
    },
    {
      key: 'uptime',
      label: t('overview.colUptime'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <ProgressBar
            pct={row.uptime}
            target={100}
            tone={row.uptime === 100 ? 'ok' : row.uptime >= 60 ? 'warn' : 'crit'}
          />
          <span className="w-10 shrink-0 text-right tabular-nums text-ink-100">{row.uptime}%</span>
        </div>
      ),
      width: '14rem',
    },
    {
      key: 'status',
      label: t('overview.colStatus'),
      render: (row) => (
        <StatChip tone={toneOf(row.worst)} label={t(`status.${row.worst}`)} />
      ),
    },
  ];

  /** Appeals today per mahalla, biggest first, top five. */
  const busiest = [
    ...fleet.kiosks
      .reduce((map, kiosk) => {
        const current = map.get(kiosk.mahalla_id);
        map.set(kiosk.mahalla_id, {
          id: kiosk.mahalla_id,
          label: kiosk.mahalla_name,
          appeals: (current?.appeals ?? 0) + (kiosk.telemetry?.work?.appeals_today ?? 0),
        });
        return map;
      }, new Map<string, { id: string; label: string; appeals: number }>())
      .values(),
  ]
    .sort((a, b) => b.appeals - a.appeals)
    .slice(0, 5);

  /**
   * The ticker's contents, worst first — and NOTHING when the province is
   * clean. A ticker that always has something to say is a ticker nobody reads.
   */
  const tickerItems: { key: string; text: string; tone: Tone }[] = [];
  if ((summary?.kpi.overdue ?? 0) > 0) {
    tickerItems.push({
      key: 'overdue',
      tone: 'crit',
      text: t('ticker.overdue', { n: String(summary?.kpi.overdue ?? 0) }),
    });
  }
  if ((summary?.kpi.unassigned ?? 0) > 0) {
    tickerItems.push({
      key: 'unassigned',
      tone: 'warn',
      text: t('ticker.unassigned', { n: String(summary?.kpi.unassigned ?? 0) }),
    });
  }
  for (const kiosk of fleet.kiosks) {
    if (statusOf(kiosk, fleet.now) !== 'critical') continue;
    tickerItems.push({
      key: kiosk.id,
      tone: 'crit',
      text: t('ticker.kiosk', { code: kiosk.short_code, mahalla: kiosk.mahalla_name }),
    });
  }

  /** The received line, for the KPI card's own sparkline. */
  const receivedSeries = (series?.items ?? []).map((point) => point.received);

  /**
   * Appeals summed by weekday across the whole series.
   *
   * Built from the SAME buckets as the line above rather than a second
   * request: two views of one dataset can never disagree, and a second query
   * with its own window is exactly how a screen ends up showing two different
   * totals for the same thing.
   */
  const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
  const weekdayTotals = new Array<number>(7).fill(0);
  for (const point of series?.items ?? []) {
    const day = new Date(point.t).getDay();
    weekdayTotals[day] = (weekdayTotals[day] ?? 0) + point.received;
  }
  const todayIndex = new Date().getDay();
  /* Monday first: the working week's own order, not the C library's. */
  const weekdayBars = [1, 2, 3, 4, 5, 6, 0].map((day) => ({
    key: WEEKDAY_KEYS[day] ?? String(day),
    label: t(`weekday.${WEEKDAY_KEYS[day] ?? 'mon'}`),
    value: weekdayTotals[day] ?? 0,
    highlight: day === todayIndex,
  }));

  /** «14-avg» — short enough for an axis end. */
  const shortDate = (date: Date): string =>
    `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;

  type StatsRow = MahallaStats['items'][number];
  const statsColumns: Column<StatsRow>[] = [
    {
      key: 'name',
      label: t('overview.colMahalla'),
      render: (row) => <span className="font-semibold">{row.mahalla_name}</span>,
    },
    { key: 'total', label: t('stats.total'), numeric: true, render: (row) => integer(row.total) },
    {
      key: 'in_review',
      label: t('stats.inReview'),
      numeric: true,
      render: (row) => <span className="text-info">{integer(row.in_review)}</span>,
    },
    {
      key: 'closed',
      label: t('stats.closed'),
      numeric: true,
      render: (row) => <span className="text-ok">{integer(row.closed)}</span>,
    },
    {
      key: 'overdue',
      label: t('stats.overdue'),
      numeric: true,
      render: (row) => (
        <span className={row.overdue > 0 ? 'font-semibold text-crit' : 'text-ink-300'}>
          {integer(row.overdue)}
        </span>
      ),
    },
    {
      key: 'median',
      label: t('stats.medianDays'),
      numeric: true,
      /* ABSENT, not zero. The API omits it when nothing has closed, and «0
         days to resolve» would be the most flattering possible lie. */
      render: (row) =>
        row.median_resolution_days === undefined ? (
          <span className="text-ink-500">{t('app.notYet')}</span>
        ) : (
          row.median_resolution_days.toFixed(1)
        ),
    },
    {
      key: 'share',
      label: t('stats.share'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <ProgressBar pct={row.share_pct ?? 0} tone="info" />
          <span className="w-10 shrink-0 text-right tabular-nums text-ink-300">
            {row.share_pct === undefined ? '—' : `${Math.round(row.share_pct)}%`}
          </span>
        </div>
      ),
      width: '11rem',
    },
  ];

  /**
   * The banner's chips, worst first — and an EMPTY LIST IS ITSELF A STATE.
   *
   * A banner with no chips reads as "not loaded yet" to somebody who has seen
   * it carry three. So a clean province says so in words rather than showing
   * nothing, which is the same rule the exception panel below follows.
   */
  const heroAlerts: { key: string; tone: Tone; label: string }[] = [];
  const criticalCount = statusCounts.get('critical') ?? 0;
  const offlineCount = statusCounts.get('offline') ?? 0;
  if (criticalCount > 0) {
    heroAlerts.push({
      key: 'critical',
      tone: 'crit',
      label: t('overview.kioskCritical', { n: String(criticalCount) }),
    });
  }
  if (offlineCount > 0) {
    heroAlerts.push({
      key: 'offline',
      tone: 'warn',
      label: t('overview.kioskOffline', { n: String(offlineCount) }),
    });
  }
  if (overview?.open_alerts && overview.open_alerts > 0) {
    heroAlerts.push({
      key: 'alerts',
      tone: 'info',
      label: t('overview.alertsOpen', { n: String(overview.open_alerts) }),
    });
  }
  if (heroAlerts.length === 0) {
    heroAlerts.push({ key: 'clear', tone: 'info', label: t('overview.allClear') });
  }

  return (
    <div className="space-y-6">
      {/*
        The banner. It carries the exceptions rather than a greeting with
        decorations: §15.7 asks this screen to answer "what is wrong today"
        first, and the chips below the title are that answer before the
        governor has scrolled or clicked anything.
      */}
      <HeroBanner
        eyebrow={
          <>
            <LiveBadge label={t('overview.liveNow')} />
            <span className="text-micro uppercase tracking-wide text-white/50">
              {overview
                ? t('overview.cityLine', {
                    mahallas: overview.mahallas,
                    kiosks: overview.kiosks_total,
                    working: overview.kiosks_working,
                  })
                : t('app.loading')}
            </span>
          </>
        }
        title={t('overview.welcome', { name: me?.full_name ?? '' })}
        subtitle={t('app.institution')}
        actions={
          <Link to="/broadcast">
            <Button>{t('overview.sendAnnouncement')}</Button>
          </Link>
        }
        alerts={heroAlerts}
      />

      {failed ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {overview?.scoped ? <Notice tone="quiet">{t('overview.scopedNote')}</Notice> : null}

      {/*
        The ticker: the single thing that would otherwise need a scroll.
        Silent when the province is clean — a permanent green banner is
        furniture people stop reading, and this line has to still work on the
        day it turns red.
      */}
      <Ticker label={t('overview.latest')} items={tickerItems} index={tick} />

      {/*
        THE PULSE STRIP — is the PLATFORM answering, as measured from here.

        The latency is this browser's own last round trip, not a health
        endpoint's opinion of itself: green means "this screen is genuinely
        talking to the platform", which is the only version of that claim worth
        putting on a wall.
      */}
      <SystemPulse
        label={fleet.error === null ? t('pulse.normal') : t('pulse.degraded')}
        ok={fleet.error === null}
        chips={[
          {
            key: 'socket',
            label: fleet.live ? t('pulse.socketLive') : t('pulse.socketPoll'),
            tone: fleet.live ? 'ok' : 'warn',
          },
          {
            key: 'kiosks',
            label: t('pulse.kiosksOnline', {
              online: String(fleet.kiosks.length - (statusCounts.get('offline') ?? 0)),
              total: String(fleet.kiosks.length),
            }),
            tone: (statusCounts.get('offline') ?? 0) === 0 ? 'ok' : 'warn',
          },
          {
            key: 'analytics',
            label: summary === null ? t('pulse.analyticsOff') : t('pulse.analyticsOn'),
            tone: summary === null ? 'neutral' : 'ok',
          },
        ]}
        series={pulse}
      />

      {/*
        EIGHT KPI CARDS, each carrying its own history and its own change.

        Four of them are appeals (the analytics stack) and four are the fleet.
        They sit together because a governor's morning question spans both:
        «are citizens being served, and are the machines that serve them
        working». Splitting those across two screens is what made this panel
        feel thin.
      */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('kpi.appealsToday')}
          value={integer(summary?.kpi.today ?? overview?.appeals_today ?? null)}
          absent={t('app.notYet')}
          series={receivedSeries}
          delta={
            overview?.appeals_yesterday === undefined || overview?.appeals_today === undefined
              ? null
              : overview.appeals_today - overview.appeals_yesterday
          }
          footerLabel={t('kpi.total')}
          footerValue={integer(summary?.kpi.total ?? null) ?? t('app.notYet')}
        />
        <KpiCard
          label={t('kpi.inProgress')}
          value={integer(summary?.kpi.in_progress ?? null)}
          absent={t('app.notYet')}
          tone="info"
          footerLabel={t('kpi.unassigned')}
          footerValue={integer(summary?.kpi.unassigned ?? null) ?? t('app.notYet')}
        />
        <KpiCard
          label={t('kpi.overdue')}
          value={integer(summary?.kpi.overdue ?? null)}
          absent={t('app.notYet')}
          tone={(summary?.kpi.overdue ?? 0) > 0 ? 'crit' : 'ok'}
          footerLabel={t('kpi.dueSoon')}
          footerValue={integer(summary?.kpi.due_soon ?? null) ?? t('app.notYet')}
        />
        <KpiCard
          label={t('kpi.onTime')}
          /* The STATUTORY figure, not the internal target. Both exist in the
             API and they answer different questions; the one a governor is
             held to is the law's. */
          value={
            summary?.kpi.statutory_on_time_pct === undefined
              ? null
              : `${Math.round(summary.kpi.statutory_on_time_pct)}%`
          }
          absent={t('app.notYet')}
          tone={
            summary?.kpi.statutory_on_time_pct === undefined
              ? 'neutral'
              : summary.kpi.statutory_on_time_pct >= 95
                ? 'ok'
                : summary.kpi.statutory_on_time_pct >= 85
                  ? 'warn'
                  : 'crit'
          }
          footerLabel={t('kpi.medianDays')}
          footerValue={
            summary?.kpi.median_resolution_days === undefined
              ? t('app.notYet')
              : summary.kpi.median_resolution_days.toFixed(1)
          }
        />

        <KpiCard
          label={t('kpi.kiosksWorking')}
          value={
            overview === null ? null : `${overview.kiosks_working}/${overview.kiosks_total}`
          }
          absent={t('app.notYet')}
          tone={
            overview !== null && overview.kiosks_working === overview.kiosks_total ? 'ok' : 'warn'
          }
          footerLabel={t('overview.mahallas')}
          footerValue={integer(overview?.mahallas ?? null) ?? t('app.notYet')}
        />
        <KpiCard
          label={t('overview.queueTotal')}
          value={integer(queueTotal)}
          absent={t('app.notYet')}
          series={pulse}
          tone={queueTotal > 0 ? 'warn' : 'ok'}
          footerLabel={t('kpi.acrossKiosks')}
          footerValue={String(fleet.kiosks.filter((k) => (k.telemetry?.work?.queue_depth ?? 0) > 0).length)}
        />
        <KpiCard
          label={t('overview.openAlerts')}
          value={integer(overview?.open_alerts ?? null)}
          absent={t('app.notYet')}
          tone={(overview?.open_alerts ?? 0) > 0 ? 'crit' : 'ok'}
          footerLabel={t('overview.critical')}
          footerValue={String(statusCounts.get('critical') ?? 0)}
        />
        <KpiCard
          label={t('overview.staffPresent')}
          value={integer(overview?.staff_present ?? null)}
          absent={t('app.notYet')}
          tone="neutral"
          footerLabel={t('kpi.repeatAppellants')}
          footerValue={integer(summary?.kpi.repeat_appellants ?? null) ?? t('app.notYet')}
        />
      </div>

      {/*
        THE PROVINCE'S APPEAL CURVE — a real 366-bucket series, day-aligned to
        Asia/Tashkent, that this panel had never once requested.

        Received against closed on ONE axis, because the gap between them IS the
        backlog. Two axes would let the lines be made to cross by choosing
        bounds, and a governor would be reading a decision out of a rendering.
      */}
      {series === null ? null : (
        <Panel>
          <PanelHeader
            title={t('overview.appealCurve')}
            aside={t('overview.appealCurveHint', { n: String(series.items.length) })}
          />
          {/*
            THE GAUGES SIT BESIDE THE CURVE, NOT ABOVE IT.

            Each is a percentage measured against a line somebody is held to —
            the statutory deadline, the internal target, the fleet's own uptime
            — and the target is DRAWN on every one of them. A ring showing 94%
            of an unstated target is decoration; the tick is what makes it a
            reading.
          */}
          <div className="grid gap-4 border-b border-hairline px-4 py-4 sm:grid-cols-3">
            <Gauge
              pct={summary?.kpi.statutory_on_time_pct ?? null}
              target={95}
              label={t('gauge.statutory')}
              tone={
                (summary?.kpi.statutory_on_time_pct ?? 0) >= 95 ? 'text-ok' : 'text-warn'
              }
            />
            <Gauge
              pct={summary?.kpi.target_on_time_pct ?? null}
              target={90}
              label={t('gauge.internal')}
              tone="text-brand"
            />
            <Gauge
              pct={
                overview === null || overview.kiosks_total === 0
                  ? null
                  : (overview.kiosks_working / overview.kiosks_total) * 100
              }
              target={100}
              label={t('gauge.fleet')}
              tone={
                overview !== null && overview.kiosks_working === overview.kiosks_total
                  ? 'text-ok'
                  : 'text-warn'
              }
            />
          </div>
          <div className="px-4 py-4">
            <DualSeries
              points={series.items.map((point) => ({
                t: point.t,
                a: point.received,
                b: point.closed,
              }))}
              labelA={t('overview.received')}
              labelB={t('overview.closed')}
              format={(iso) => shortDate(new Date(iso))}
            />
          </div>
          {/*
            WHICH DAYS ARE BUSY — the question the line cannot answer.

            The same buckets, summed by weekday. A daily line shows the trend
            and hides the rhythm; a governor deciding when to staff a qabulxona
            needs the rhythm. Today's column is highlighted so the reader knows
            where they are standing in it.
          */}
          <div className="border-t border-hairline px-4 py-4">
            <p className="mb-3 text-micro font-semibold uppercase tracking-wide text-ink-500">
              {t('overview.byWeekday')}
            </p>
            <BarChart bars={weekdayBars} />
          </div>
        </Panel>
      )}

      {/*
        THE MAHALLA STATISTICS TABLE — the other unused endpoint.

        This is the table the house console leads with, and the data for it has
        been sitting behind `/admin/stats/mahallas` the whole time: totals,
        overdue counts, share of the province, and the median days to resolve.
        `share_pct` and the medians are ABSENT rather than zero when there is
        nothing to divide by, and are rendered as absent.
      */}
      {mahallaStats === null ? null : (
        <Panel>
          <PanelHeader
            title={t('overview.appealsByMahalla')}
            aside={
              mahallaStats.scoped ? t('fleet.scopedNote') : t('overview.appealsByMahallaHint')
            }
          />
          <DataTable
            columns={statsColumns}
            rows={[...mahallaStats.items].sort((a, b) => b.overdue - a.overdue || b.total - a.total)}
            keyOf={(row) => row.mahalla_id}
            total={[
              t('overview.totalRow'),
              integer(mahallaStats.items.reduce((sum, row) => sum + row.total, 0)),
              integer(mahallaStats.items.reduce((sum, row) => sum + row.in_review, 0)),
              integer(mahallaStats.items.reduce((sum, row) => sum + row.closed, 0)),
              integer(mahallaStats.items.reduce((sum, row) => sum + row.overdue, 0)),
              '',
              '',
            ]}
          />
        </Panel>
      )}

      {/*
        THE FIVE DISTRIBUTIONS, all zero-filled by the API on purpose: a
        channel with no appeals today is a fact worth seeing, and a missing key
        would be silently dropped by the response schema rather than shown as 0.
      */}
      {breakdown === null ? null : (
        <Panel>
          <PanelHeader title={t('overview.breakdown')} aside={t('overview.breakdownHint')} />
          <div className="grid gap-6 px-4 py-4 sm:grid-cols-2 xl:grid-cols-4">
            <Distribution
              title={t('overview.byChannel')}
              rows={Object.entries(breakdown.by_channel).map(([key, value]) => ({
                key,
                label: hasKey(`channel.${key}`) ? t(`channel.${key}`) : key,
                value: value as number,
              }))}
            />
            <Distribution
              title={t('overview.byKind')}
              rows={Object.entries(breakdown.by_kind).map(([key, value]) => ({
                key,
                label: hasKey(`kind.${key}`) ? t(`kind.${key}`) : key,
                value: value as number,
              }))}
            />
            <Distribution
              title={t('overview.byLocale')}
              rows={Object.entries(breakdown.by_locale).map(([key, value]) => ({
                key,
                label: key,
                value: value as number,
              }))}
            />
            <Distribution
              title={t('overview.byLegalType')}
              rows={Object.entries(breakdown.by_legal_type).map(([key, value]) => ({
                key,
                label: hasKey(`legal.${key}`) ? t(`legal.${key}`) : key,
                value: value as number,
              }))}
            />
          </div>
        </Panel>
      )}

      {/*
        THE RANKING TABLE — the screen's centre of gravity.

        A governor's actual question is comparative: which mahalla is behind,
        and by how much. Four headline figures cannot answer that however
        prominently they are set, and a list of sentences answers it only for
        the two worst. The table answers it for all of them at once, sorted by
        risk by default so the answer is already on top.
      */}
      <Panel>
        <PanelHeader
          title={t('overview.ranking')}
          aside={
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-micro text-ink-500">{t('overview.rankingHint')}</span>
              <SegmentedControl
                value={sort}
                onChange={setSort}
                options={[
                  { value: 'risk', label: t('overview.sortRisk') },
                  { value: 'appeals', label: t('overview.sortAppeals') },
                  { value: 'uptime', label: t('overview.sortUptime') },
                  { value: 'name', label: t('overview.sortName') },
                ]}
              />
            </div>
          }
        />
        {fleet.loading ? (
          <div className="px-4 py-4">
            <Skeleton className="h-64" />
          </div>
        ) : sorted.length === 0 ? (
          <p className="px-4 py-6 text-label text-ink-500">{t('app.notYet')}</p>
        ) : (
          <DataTable
            columns={columns}
            rows={sorted}
            keyOf={(row) => row.id}
            /* A TOTAL ROW, because every figure above it is a share of
               something and a reader who cannot see the denominator is reading
               percentages of an unknown. */
            total={[
              '',
              t('overview.totalRow'),
              integer(sorted.reduce((sum, row) => sum + row.appeals, 0)),
              integer(sorted.reduce((sum, row) => sum + row.queue, 0)),
              '',
              '',
            ]}
          />
        )}
      </Panel>

      {/* Four figures only (§15.7). */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {overview === null ? (
          <>
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </>
        ) : (
          <>
            <KpiTile
              label={t('overview.kiosksWorking')}
              value={`${overview.kiosks_working}/${overview.kiosks_total}`}
              absent={t('app.notYet')}
              tone={overview.kiosks_working === overview.kiosks_total ? 'ok' : 'warn'}
              footer={
                <ProgressBar
                  pct={
                    overview.kiosks_total === 0
                      ? 0
                      : (overview.kiosks_working / overview.kiosks_total) * 100
                  }
                  target={100}
                  tone={overview.kiosks_working === overview.kiosks_total ? 'ok' : 'warn'}
                />
              }
            />
            <KpiTile
              label={t('overview.staffPresent')}
              value={integer(overview.staff_present)}
              absent={t('app.notYet')}
            />
            <KpiTile
              label={t('overview.appealsToday')}
              value={integer(overview.appeals_today)}
              absent={t('app.notYet')}
              delta={
                overview.appeals_yesterday === undefined
                  ? null
                  : overview.appeals_today - overview.appeals_yesterday
              }
              deltaLabel={t('overview.vsYesterday')}
            />
            <KpiTile
              label={t('overview.openAlerts')}
              value={integer(overview.open_alerts)}
              absent={t('app.notYet')}
              tone={overview.open_alerts && overview.open_alerts > 0 ? 'crit' : 'neutral'}
              /* Down is GOOD here. Without the inversion a falling backlog
                 would be painted red as though it were a failure. */
              invertDelta
            />
          </>
        )}
      </div>

      {/*
        The fleet at a glance, and the province's load beside it.

        §15.7 caps the FIGURES at four, and that cap is about headline numbers
        competing for one glance — it is not an argument against showing the
        shape behind them. The ring answers "how is the fleet split", the bars
        answer "where is the work", and neither is a number the four figures
        already carry.
      */}
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel>
          <PanelHeader title={t('overview.fleetSplit')} />
          <div className="px-4 py-4">
            {fleet.loading ? (
              <Skeleton className="h-32" />
            ) : (
              <StatusDonut
                centreValue={String(statusCounts.get('healthy') ?? 0)}
                centreLabel={t('status.healthy')}
                slices={STATUS_ORDER.filter((status) => (statusCounts.get(status) ?? 0) > 0).map(
                  (status) => ({
                    key: status,
                    label: t(`status.${status}`),
                    value: statusCounts.get(status) ?? 0,
                    // `?? ''` only to satisfy the index signature — every
                    // status in STATUS_ORDER has an entry above, and a missing
                    // one would render an uncoloured (but still labelled) slice
                    // rather than crash the governor's home screen.
                    className: STATUS_TONE[status] ?? '',
                  }),
                )}
              />
            )}
          </div>
        </Panel>

        <Panel>
          <PanelHeader title={t('overview.busiest')} aside={t('overview.busiestHint')} />
          <div className="space-y-2 px-4 py-4">
            {fleet.loading ? (
              <Skeleton className="h-32" />
            ) : busiest.length === 0 ? (
              <p className="text-label text-ink-500">{t('app.notYet')}</p>
            ) : (
              busiest.map((entry) => (
                <BarRow
                  key={entry.id}
                  label={entry.label}
                  value={entry.appeals}
                  max={busiest[0]?.appeals ?? 1}
                  display={integer(entry.appeals) ?? '0'}
                />
              ))
            )}
          </div>
        </Panel>
      </div>

      {/*
        The province's own pulse: queue depth summed across the fleet, sampled
        every time this screen polls. It is IN-SESSION ONLY and says so — a
        sparkline that quietly restarted at every page load while looking like
        history would be worse than no sparkline.
      */}
      <Panel>
        <PanelHeader title={t('overview.pulse')} aside={t('overview.pulseHint')} />
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-3">
          <div>
            <p className="text-micro uppercase tracking-wide text-ink-500">
              {t('overview.queueTotal')}
            </p>
            <p className="text-figure tabular-nums text-ink-100">{integer(queueTotal)}</p>
            <Sparkline values={pulse} />
          </div>
          <div className="sm:col-span-2 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KpiTile
              label={t('overview.mahallas')}
              value={integer(overview?.mahallas ?? null)}
              absent={t('app.notYet')}
            />
            <KpiTile
              label={t('overview.offline')}
              value={String(statusCounts.get('offline') ?? 0)}
              absent={t('app.notYet')}
              tone={(statusCounts.get('offline') ?? 0) > 0 ? 'warn' : 'neutral'}
            />
            <KpiTile
              label={t('overview.critical')}
              value={String(statusCounts.get('critical') ?? 0)}
              absent={t('app.notYet')}
              tone={(statusCounts.get('critical') ?? 0) > 0 ? 'crit' : 'neutral'}
            />
            <KpiTile
              label={t('overview.maintenance')}
              value={String(statusCounts.get('maintenance') ?? 0)}
              absent={t('app.notYet')}
            />
          </div>
        </div>
      </Panel>

      {/*
        «Tezkor amallar» — the six places a governor goes from here.

        Real navigation, not a decorative tile wall: every one is a route this
        session can actually open, and the row is filtered by capability so
        nobody is offered a door that refuses them. Adding a tile that opened
        nothing would be the kind of density this redesign is supposed to avoid.
      */}
      <Panel>
        <PanelHeader title={t('overview.quickActions')} />
        <div className="grid grid-cols-2 gap-3 px-4 py-4 sm:grid-cols-3 xl:grid-cols-6">
          {QUICK_ACTIONS.filter((action) => allows(action.capability)).map((action) => (
            <Link
              key={action.to}
              to={action.to}
              className="flex flex-col items-center gap-2 rounded-card border border-hairline bg-panel px-3 py-4 text-center transition-colors duration-state hover:bg-panel-raised"
            >
              <action.Icon className="h-6 w-6 text-brand" />
              <span className="text-micro font-medium text-ink-100">{t(action.labelKey)}</span>
            </Link>
          ))}
        </div>
      </Panel>

      {/* Today's exceptions, in sentences (§15.7). */}
      <Panel>
        <PanelHeader
          title={t('overview.attention')}
          aside={
            fleet.fetchedAt
              ? t('app.asOf', { at: ageLabel(fleet.fetchedAt, fleet.now, t) })
              : undefined
          }
        />
        <div className="px-4 py-3">
          {fleet.loading ? (
            <Skeleton className="h-6 w-2/3" />
          ) : exceptions.length === 0 ? (
            // §9.1's exception-first framing needs an explicit, calm "everything
            // normal" state — not an empty panel, which reads as a broken one.
            <p className="text-label text-ink-500">{t('overview.allNormal')}</p>
          ) : (
            <ul className="space-y-2">
              {exceptions.map((exception) => (
                <li key={exception.id} className="text-label text-ink-100">
                  {exception.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {/* One button (§15.7). It leads to the composer, which is P2 — so it goes
          to the honest placeholder rather than nowhere. A dead control on the
          governor's home is the one thing this screen must not have. */}
      <div>
        <Link to="/broadcast">
          <Button>{t('overview.sendAnnouncement')}</Button>
        </Link>
      </div>
    </div>
  );
}
